import React, { Fragment, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { t } from 'ttag';
import clsx from 'clsx';

import { Dialog, Disclosure, Transition } from '@headlessui/react';
import { RiArrowDownSLine, RiFilter2Fill } from 'react-icons/ri';

import { Button, Skeleton } from 'components';

import FilterButton from './FilterButton';

export default function Filters({
  metaInformation,
  isLoading,
  pathname,
  search,
  push
}) {
  const [showFilters, setShowFilters] = useState(false);
  const [filterData, setFilterData] = useState([]);

  useEffect(() => {
    if (metaInformation?.filters && filterData.length === 0) {
      setFilterData(metaInformation.filters);
    }
  }, [metaInformation]);

  useEffect(() => {
    setFilterData([]);
  }, [pathname]);

  useEffect(() => {
    if (metaInformation?.filters) {
      const searchParams = new URLSearchParams(search);

      setFilterData(prevState => {
        return metaInformation.filters.map(filter => {
          if (searchParams.has(filter.key)) {
            return (
              prevState.find(prevFilter => prevFilter.key === filter.key) ||
              filter
            );
          }
          return filter;
        });
      });
    }
  }, [metaInformation]);

  const handleFilterChange = useCallback(
    (key, value) => {
      const url = new URL(window.location.href);
      const values = url.searchParams
        .getAll(key)
        .join('|')
        .split('|')
        .filter(v => v !== '');

      if (values.includes(value)) {
        values.splice(values.indexOf(value), 1);
      } else {
        values.push(value);
      }

      if (values.length > 0) {
        url.searchParams.set(key, values.join('|'));
      } else {
        url.searchParams.delete(key);
      }

      // We always want to reset the page, when the Customer applies a filter.
      if (url.searchParams.has('page')) {
        url.searchParams.delete('page');
      }

      push(url);
    },
    [metaInformation]
  );

  const handleFilterReset = useCallback(() => {
    if (metaInformation?.filters) {
      const url = new URL(window.location.href);
      Object.keys(metaInformation.filters).forEach(key => {
        const filter = metaInformation.filters[key];
        if (url.searchParams.has(filter.key)) {
          url.searchParams.delete(filter.key);
        }
      });

      // We always want to reset the page, when the Customer resets the filters.
      if (url.searchParams.has('page')) {
        url.searchParams.delete('page');
      }
      push(url);
    }
  }, [metaInformation]);

  const isFilterApplied = useCallback(
    (key, value) => {
      const searchParams = new URLSearchParams(search);
      const values = searchParams.getAll(key).join('|').split('|');
      return values.includes(value);
    },
    [search]
  );

  const getFilterCount = useCallback(() => {
    const searchParams = new URLSearchParams(search);
    const appliedFilters = filterData.filter(filter =>
      searchParams.has(filter.key)
    );
    // Get the total amount of values from all applied filters in the Search Params.
    return appliedFilters.reduce(
      (acc, filter) =>
        acc +
        searchParams
          .getAll(filter.key)
          .join('|')
          .split('|')
          .filter(v => v !== '').length,
      0
    );
  }, [search, filterData]);

  return (
    <>
      <Skeleton
        width={160}
        height={38}
        isLoading={metaInformation?.filters === undefined}
      >
        <div className="sm:w-[160px]">
          <Button
            variant="primary"
            size="sm"
            icon={<RiFilter2Fill className="h-4 w-4 shrink-0" />}
            onClick={() => setShowFilters(!showFilters)}
            disabled={isLoading}
            isFullWidth
          >
            {t`Filters`} ({getFilterCount()})
          </Button>
        </div>
      </Skeleton>
      <Transition show={showFilters} as={Fragment}>
        <Dialog onClose={() => setShowFilters(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="fixed inset-0 z-[150] cursor-pointer bg-black/30"
              aria-hidden="true"
            />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="ease-in duration-150"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="fixed inset-0 z-[150] bg-white sm:inset-auto sm:inset-y-0 sm:left-0">
              <Dialog.Panel className="relative flex h-full w-full flex-col gap-4 overflow-y-scroll p-4 sm:w-[470px]">
                {/** Header */}
                <header className="mb-8 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <RiFilter2Fill className="h-4 w-4 text-night-rider" />
                    <Dialog.Title
                      as="h1"
                      className="m-0 text-lg font-semibold leading-none text-night-rider"
                    >
                      {t`Filters`}
                    </Dialog.Title>
                  </div>
                  <button
                    type="button"
                    aria-label={t`Close`}
                    onClick={() => setShowFilters(false)}
                    className="m-0 flex items-center border-none bg-transparent text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-deep-blue focus-visible:ring-offset-2"
                    tabIndex={-1}
                  >
                    {t`Close`}
                  </button>
                </header>

                {/** Currently Selected Filters */}
                <div className="flex items-center justify-between gap-4">
                  <h2 className="m-0 text-base font-semibold text-night-rider md:text-sm">
                    {getFilterCount() === 0
                      ? t`No Filters Selected`
                      : t`Currently Selected Filters`}
                  </h2>
                  <button
                    type="button"
                    aria-label={t`Close`}
                    onClick={() => {
                      handleFilterReset();
                    }}
                    className="m-0 flex items-center border-none bg-transparent text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-deep-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    tabIndex={-1}
                    disabled={isLoading}
                  >
                    {t`Clear All`}
                  </button>
                </div>
                <ul className="m-0 flex list-none flex-row flex-wrap gap-2">
                  {filterData?.length &&
                    filterData.map(filter =>
                      filter.values
                        .filter(
                          value =>
                            isFilterApplied(filter.key, value.name) &&
                            value.name !== ''
                        )
                        .map(value => (
                          <li key={value.name}>
                            <FilterButton
                              value={value}
                              onClick={() =>
                                handleFilterChange(filter.key, value.name)
                              }
                              isApplied={isFilterApplied(
                                filter.key,
                                value.name
                              )}
                              isClosable
                              disabled={isLoading}
                            />
                          </li>
                        ))
                    )}
                </ul>

                {/** Available Filters */}
                {filterData &&
                  filterData.map(
                    (filter, index) =>
                      /** @type {React.ReactNodeArray} */ (
                        (
                          <Disclosure key={filter.key}>
                            {({ open }) => (
                              <>
                                <Disclosure.Button
                                  className={clsx(
                                    'm-0 flex items-center justify-between border-0 border-solid border-whisper bg-transparent p-0 py-4 text-base focus:outline-none md:text-sm',
                                    {
                                      'border-t': index !== 0
                                    }
                                  )}
                                >
                                  <span>{filter.name}</span>
                                  <RiArrowDownSLine
                                    className={clsx(
                                      'h-4 w-4',
                                      open ? 'rotate-180 transform' : ''
                                    )}
                                  />
                                </Disclosure.Button>
                                <Disclosure.Panel
                                  as="ul"
                                  className="m-0 flex list-none flex-row flex-wrap gap-2"
                                >
                                  {filter?.values &&
                                    filter.values
                                      .filter(value => value.name !== '')
                                      .map(
                                        value =>
                                          /** @type {React.ReactNode} */ (
                                            (
                                              <li key={value.name}>
                                                <FilterButton
                                                  value={value}
                                                  onClick={() =>
                                                    handleFilterChange(
                                                      filter.key,
                                                      value.name
                                                    )
                                                  }
                                                  isApplied={isFilterApplied(
                                                    filter.key,
                                                    value.name
                                                  )}
                                                  disabled={isLoading}
                                                />
                                              </li>
                                            )
                                          )
                                      )}
                                </Disclosure.Panel>
                              </>
                            )}
                          </Disclosure>
                        )
                      )
                  )}

                {/** Apply Filters Button */}
                <div className="sticky inset-x-0 bottom-0 bg-white">
                  <Button
                    variant="primary"
                    onClick={() => setShowFilters(false)}
                    isFullWidth
                  >
                    {t`Show Results`}
                    {` `}({metaInformation?.total})
                  </Button>
                </div>
              </Dialog.Panel>
            </div>
          </Transition.Child>
        </Dialog>
      </Transition>
    </>
  );
}

Filters.propTypes = {
  /**
   * The Meta Information returned by the API.
   */
  // eslint-disable-next-line react/forbid-prop-types
  metaInformation: PropTypes.object,
  /**
   * Determines if the Meta Information is being loaded.
   */
  isLoading: PropTypes.bool.isRequired,
  /**
   * The current Path Name of the Page.
   */
  pathname: PropTypes.string.isRequired,
  /**
   * The Search Query.
   */
  search: PropTypes.string.isRequired,
  /**
   * The Push function from useLocation()
   */
  push: PropTypes.func.isRequired
};

Filters.defaultProps = {
  metaInformation: undefined
};
